// @ts-strict-ignore
import React, {
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  IconName,
  Text,
} from '@usealloy/component-library';

import { ActionType } from 'flow/store/engine/constants';
import type { Action } from 'flow/store/engine/types';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';

import { truncate } from '~/helpers/formatting';
import { routes } from '~/helpers/routes';
import { useAppSelector } from '~/store/reduxHooks';

import {
  SubHeader,
  WorkflowNode,
  Wrapper,
} from './styles';

type Props = {
  action: Action;
};

export function ImportedAttributeDetails({
  action,
}: Props) {
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);

  const { workflowsOutcomeReasonTagsByTagId } = workflowData;
  const {
    importableAttributes,
    tags,
  } = workflowProps;

  const getWorkflowsByTag = useCallback(name => {
    const tagId = tags.find(
      tag => tag.tag_name === name,
    )?.customer_tag_id;

    return workflowsOutcomeReasonTagsByTagId[tagId]?.filter(workflow => !workflow.archived);
  }, [
    tags,
    workflowsOutcomeReasonTagsByTagId,
  ]);

  const getWorkflowsByImportableAttribute = ({
    attributes,
    name,
  }) => (attributes[name] ? attributes[name]
    .map(workflow => ({
      name: workflow.workflow_name,
      version: workflow.workflow_version,
      token: workflow.workflow_token,
    }))
    : []);

  const workflows = useMemo(() => {
    if (action.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
      return getWorkflowsByImportableAttribute({
        attributes: importableAttributes.output_attributes,
        name: action.importedOutputAttribute.displayName,
      });
    }

    if (action.type === ActionType.IMPORTED_MATRIX_MODEL) {
      return getWorkflowsByImportableAttribute({
        attributes: importableAttributes.matrix_models,
        name: action.importedMatrixModel.displayName,
      });
    }

    if (action.type === ActionType.OUTCOME_REASON) {
      return getWorkflowsByTag(action.outcomeReason.displayName);
    }

    return [];
  }, [
    action.importedMatrixModel?.displayName,
    action.importedOutputAttribute?.displayName,
    action.outcomeReason?.displayName,
    action.type,
    importableAttributes.matrix_models,
    importableAttributes.output_attributes,
    getWorkflowsByTag,
  ]);

  return workflows?.length > 0
    ? (
      <Box
        alignItems="flex-start"
        direction="column"
        justifyContent="flex-start"
        style={{ padding: 'var(--cl-space-5)' }}
      >
        <SubHeader size={1}>
          Imported from Workflow(s):
        </SubHeader>
        <Box
          alignItems="flex-start"
          direction="column"
          justifyContent="flex-start"
        >
          {workflows.map((workflow, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Wrapper key={`${idx}-${workflow.name}`}>
              <WorkflowNode
                buttonOnClick={() => { }}
                iconName={IconName.Workflows}
                isNonInteractive
                isSelected={false}
                isWorkflowActive
                onClick={() => {
                  window.open(
                    routes.applications.versions.update(
                      workflow.token,
                      workflow.version,
                    ),
                  );
                }}
                status="neutral"
                style={{ cursor: 'pointer' }}
                workflowVersionNumber={workflow.version}
              >
                <Text>{truncate(workflow.name, 20)}</Text>
              </WorkflowNode>
            </Wrapper>
          ))}
        </Box>
      </Box>
    ) : (
      <Box
        justifyContent="flex-start"
        style={{ padding: 'var(--cl-space-5)' }}
      >
        <Text
          size={1}
          style={{ color: 'var(--cl-text-secondary)' }}
        >
          Imported but currently not used in an active Workflow
        </Text>
      </Box>
    );
}
